import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { logOut } from '../../reducers/authorization-slice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { paths, roles } from '../../routes/paths';
import { useAppRole, useRolePath } from '../../routes/useRolePath';
import { getAdminSignInUrl } from '../../infrastructure/http/apiClient';
import { userApi } from '../../../domains/user/infrastructure/api/user.api';
import { useUserProfile } from '../../../domains/user/ui/hooks/useUserProfile';
import { useNotificationsCount } from '../../../domains/notification/ui/hooks/useNotificationsCount';
import { useFaqList } from '../../../domains/faq/ui/hooks/useFaqList';
import profileCabinetIcon from '../../../assets/images/svgs/profile_cabinet.svg';
import logoutCabinetIcon from '../../../assets/images/svgs/logout_cabinet.svg';
import avatarMan from '../../../assets/images/avatars/avatar-man.svg';
import { useSearchSuggestions } from '../../../domains/search';
import type { SearchSuggestion } from '../../components/SearchBar/SearchBarAutocomplete';
import HeaderView from './Header.view';
import type { HeaderMenuItem, HeaderNavigationItem, HeaderProps } from './Header.types';

const Header = ({
  className,
  showProfile,
  showNotifications,
  showLanguageSelector = true,
  showSignIn,
  navigationItems,
}: HeaderProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const to = useRolePath();
  const appRole = useAppRole();
  const accessToken = useAppSelector((state) => state.authorization.accessToken);
  const authUser = useAppSelector((state) => state.authorization.user);
  const { view: profileView } = useUserProfile();

  const isAuthenticated = Boolean(accessToken);
  const resolvedShowProfile = showProfile ?? isAuthenticated;
  const resolvedShowNotifications = showNotifications ?? isAuthenticated;
  const resolvedShowSignIn = showSignIn ?? !isAuthenticated;
  const { count: notificationsCount } = useNotificationsCount(!isAuthenticated);

  const { items: faqItems, isLoading: isFaqLoading } = useFaqList({
    skip: isAuthenticated,
  });

  const [isMenuOpen, setMenuOpen] = useState(false);
  const [isNotificationsOpen, setNotificationsOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setMenuOpen(false);
      setNotificationsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const resolvedNavigationItems = useMemo<HeaderNavigationItem[]>(() => {
    if (navigationItems) {
      return navigationItems;
    }

    if (isAuthenticated) {
      return [
        {
          id: 'mentors',
          label: t('header.navigation.mentors'),
          route: to(paths.mentors),
        },
        {
          id: 'contacts',
          label: t('header.navigation.contacts'),
          route: `${paths.aboutUs}#contacts`,
        },
        {
          id: 'office',
          label: t('navigation.office'),
          href: getAdminSignInUrl(),
        },
      ];
    }

    const guestItems: HeaderNavigationItem[] = [
      {
        id: 'about-us',
        label: t('header.navigation.about-us'),
        route: paths.aboutUs,
      },
      {
        id: 'mentors',
        label: t('header.navigation.mentors'),
        route: to(paths.mentors),
      },
    ];

    if (!isFaqLoading && faqItems.length > 0) {
      guestItems.push({
        id: 'faq',
        label: t('header.navigation.faq'),
        route: '/#faq',
      });
    }

    guestItems.push({
      id: 'contacts',
      label: t('footer.contact'),
      route: `${paths.aboutUs}#contacts`,
    });

    return guestItems;
  }, [navigationItems, isAuthenticated, t, to, isFaqLoading, faqItems.length]);

  const displayName =
    profileView?.displayName ||
    [authUser?.firstName, authUser?.lastName].filter(Boolean).join(' ').trim();

  const menuItems = useMemo<HeaderMenuItem[]>(() => {
    if (!resolvedShowProfile) {
      return [];
    }

    return [
      {
        id: 'profile',
        title: displayName,
        route: to(paths.account),
        iconClass: 'profile_cabinet_icon',
        iconSrc: avatarMan,
        onClick: () => setMenuOpen(false),
      },
      ...(appRole === roles.MENTOR
        ? [
            {
              id: 'education-profile',
              title: t('navigation.education_profile'),
              route: to(paths.profile),
              iconClass: 'profile_cabinet_icon',
              iconSrc: profileCabinetIcon,
              onClick: () => setMenuOpen(false),
            } satisfies HeaderMenuItem,
          ]
        : []),
      ...(appRole === roles.CLIENT
        ? [
            {
              id: 'education-profile',
              title: t('navigation.student_education_profile'),
              route: to(paths.profile),
              iconClass: 'profile_cabinet_icon',
              iconSrc: profileCabinetIcon,
              onClick: () => setMenuOpen(false),
            } satisfies HeaderMenuItem,
          ]
        : []),
      {
        id: 'office',
        title: t('navigation.office'),
        href: getAdminSignInUrl(),
        iconClass: 'profile_cabinet_icon',
        iconSrc: profileCabinetIcon,
        onClick: () => setMenuOpen(false),
      },
      {
        id: 'logout',
        title: t('navigation.log_out'),
        route: paths.home,
        iconClass: 'profile_cabinet_icon',
        iconSrc: logoutCabinetIcon,
        onClick: () => {
          dispatch(userApi.util.resetApiState());
          dispatch(logOut());
        },
      },
    ];
  }, [resolvedShowProfile, displayName, to, t, dispatch, appRole]);

  const { fetchSuggestions: onHeaderSuggestions } = useSearchSuggestions();

  const onHeaderSuggestionSelect = useCallback(
    (suggestion: SearchSuggestion) => {
      if (!suggestion.link) return;
      navigate(suggestion.link);
    },
    [navigate],
  );

  const onHeaderSearch = useCallback(
    (value: string) => {
      const q = value.trim();
      if (!q) {
        navigate(to(paths.search));
        return;
      }
      navigate(`${to(paths.search)}?q=${encodeURIComponent(q)}`);
    },
    [navigate, to],
  );

  return (
    <HeaderView
      className={className}
      navigationItems={resolvedNavigationItems}
      showSignIn={resolvedShowSignIn}
      showLanguageSelector={showLanguageSelector}
      showNotifications={resolvedShowNotifications}
      showProfile={resolvedShowProfile}
      signInLabel={t('header.navigation.sign-in')}
      onSignIn={() => navigate(paths.signIn)}
      profilePicture={profileView?.avatarUrl}
      profileGender={profileView?.gender}
      isMenuOpen={isMenuOpen}
      isNotificationsOpen={isNotificationsOpen}
      onProfileMenuToggle={() => setMenuOpen(true)}
      onNotificationsToggle={() => navigate(to(paths.conversations))}
      notificationsCount={notificationsCount}
      menuItems={menuItems}
      menuRef={menuRef}
      triggerRef={triggerRef}
      onHeaderSearch={onHeaderSearch}
      onHeaderSuggestions={onHeaderSuggestions}
      onHeaderSuggestionSelect={onHeaderSuggestionSelect}
    />
  );
};

export default Header;
